import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import type { OktaConfig } from './okta-config';

export type { OktaConfig };
import { Security } from '@okta/okta-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCadAuth } from '../cad/CadAuthProvider';
import { CadAuthProvider } from '../cad/CadAuthProvider';
import { useCadConfig } from '../cad/cad-config';
import { CadConfigProvider } from '../cad/cad-config';
import { useAuthState as useOktaAuthState } from '../hooks/use-auth-state';

// ---------------------------------------------------------------------------
// Unified Auth types
// ---------------------------------------------------------------------------

interface BaseAuth {
  isAuthenticated: boolean;
  isPending: boolean;
  user: { email?: string; name?: string; sub?: string } | null;
  getToken: () => Promise<string>;
  logout: () => Promise<void>;
  login: () => Promise<void>;
}

export interface OktaAuthInfo extends BaseAuth {
  provider: 'okta';
}

export interface CadAuthInfo extends BaseAuth {
  provider: 'cad';
}

export type Auth = OktaAuthInfo | CadAuthInfo;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<Auth>({
  provider: 'okta',
  isAuthenticated: false,
  isPending: true,
  user: null,
  getToken: async () => '',
  logout: async () => {},
  login: async () => {},
});

export function useAuth(): Auth {
  return useContext(AuthContext);
}

// ---------------------------------------------------------------------------
// Internal: provides the active OktaAuth instance to nested components
// (e.g., the unified LoginCallback). Apps should not consume this directly.
// ---------------------------------------------------------------------------

const OktaAuthInstanceContext = createContext<OktaAuth | null>(null);

/** @internal — used by the unified LoginCallback */
export function useOktaAuthInstance(): OktaAuth | null {
  return useContext(OktaAuthInstanceContext);
}

// ---------------------------------------------------------------------------
// Module-level token provider — set by whichever bridge is active.
// Api-clients call getToken() instead of managing tokens themselves.
// ---------------------------------------------------------------------------

const _tokenBridge = {
  getToken: async (): Promise<string> => '',
  refreshToken: async (): Promise<boolean> => false,
  login: async (): Promise<void> => {},
};

export function getToken(): Promise<string> {
  return _tokenBridge.getToken();
}

/**
 * Attempt to refresh the access token. Returns true if successful.
 * API clients call this on 401 before retrying.
 */
export function refreshAccessToken(): Promise<boolean> {
  return _tokenBridge.refreshToken();
}

/**
 * Send the user to a visible sign-in via the active provider (CAD authorize
 * redirect or Okta signInWithRedirect). API clients call this on an
 * unrecoverable 401 (refresh failed or the retry still 401'd) instead of
 * failing silently.
 */
export function redirectToLogin(): Promise<void> {
  return _tokenBridge.login();
}

// ---------------------------------------------------------------------------
// Internal: bridges CAD auth into unified Auth
// ---------------------------------------------------------------------------

function CadAuthBridge({ children }: { children: ReactNode }) {
  const cadAuth = useCadAuth();

  // oidc-client-ts (the sessionStorage userStore) is the source of truth.
  // getAccessToken reads the token straight from oidc-client-ts and validates it
  // before use (refreshing first if User.expired), so a stale render-state token
  // is never handed to a request.
  const getToken = useCallback(() => cadAuth.getAccessToken(), [cadAuth]);
  const refreshToken = useCallback(() => cadAuth.refreshToken(), [cadAuth]);
  // Terminal-auth-failure path (api-client's redirectToLogin): clear the stored
  // session so the invalid token isn't re-sent on reload, then re-authenticate.
  const reauthenticate = useCallback(async () => {
    await cadAuth.clearSession();
    await cadAuth.signinRedirect();
  }, [cadAuth]);

  // Set bridge synchronously so it's available before children render
  _tokenBridge.getToken = getToken;
  _tokenBridge.refreshToken = refreshToken;
  _tokenBridge.login = reauthenticate;

  const value = useMemo<Auth>(
    () => ({
      provider: 'cad',
      isAuthenticated: cadAuth.isAuthenticated,
      isPending: cadAuth.isLoading,
      user: cadAuth.user?.profile
        ? {
            email: (cadAuth.user.profile as { email?: string }).email,
            name: (cadAuth.user.profile as { name?: string }).name,
            sub: (cadAuth.user.profile as { sub?: string }).sub,
          }
        : null,
      getToken,
      logout: async () => cadAuth.signOut(),
      login: async () => cadAuth.signinRedirect(),
    }),
    [cadAuth, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Internal: bridges Okta auth into unified Auth
// ---------------------------------------------------------------------------

function OktaAuthBridge({
  oktaAuth,
  children,
}: {
  oktaAuth: OktaAuth;
  children: ReactNode;
}) {
  const authState = useOktaAuthState(oktaAuth);
  const location = useLocation();

  const getToken = useCallback(async () => {
    const tokenObj = await oktaAuth.tokenManager.get('accessToken');
    return (
      (tokenObj && 'accessToken' in tokenObj
        ? (tokenObj as { accessToken?: string }).accessToken
        : '') || ''
    );
  }, [oktaAuth]);

  useEffect(() => {
    _tokenBridge.getToken = getToken;
    // Okta has no refresh_token grant wired through the bridge; on an
    // unrecoverable 401 the API client falls back to a visible sign-in.
    _tokenBridge.login = async () => {
      await oktaAuth.signInWithRedirect();
    };
  }, [getToken, oktaAuth]);

  // Track the original URI so the user is returned to it after Okta login.
  // Only relevant when not yet authenticated.
  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      const fullPath = location.pathname + location.search + location.hash;
      oktaAuth.setOriginalUri(fullPath);
    }
  }, [
    authState,
    location.pathname,
    location.search,
    location.hash,
    oktaAuth,
  ]);

  const value = useMemo<Auth>(
    () => ({
      provider: 'okta',
      isAuthenticated: authState?.isAuthenticated ?? false,
      isPending: !authState,
      user: authState?.idToken
        ? {
            email: authState.idToken.claims?.email as string | undefined,
            name: authState.idToken.claims?.name as string | undefined,
            sub: authState.idToken.claims?.sub as string | undefined,
          }
        : null,
      getToken,
      logout: async () => {
        await oktaAuth.signOut();
      },
      login: async () => {
        await oktaAuth.signInWithRedirect();
      },
    }),
    [authState, oktaAuth, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Internal: Okta Security wrapper with initialization
// ---------------------------------------------------------------------------

function OktaSecurityWrapper({
  oktaAuth,
  children,
}: {
  oktaAuth: OktaAuth;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    oktaAuth
      .start()
      .catch((err) => console.error('Failed to start OktaAuth:', err));
  }, [oktaAuth]);

  const restoreOriginalUri = useCallback(
    async (_: unknown, originalUri: string) => {
      navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
        replace: true,
      });
    },
    [navigate],
  );

  const handleAuthRequired = useCallback(
    async () => oktaAuth.signInWithRedirect(),
    [oktaAuth],
  );

  return (
    <Security
      oktaAuth={oktaAuth}
      restoreOriginalUri={restoreOriginalUri}
      onAuthRequired={handleAuthRequired}
    >
      <OktaAuthBridge oktaAuth={oktaAuth}>{children}</OktaAuthBridge>
    </Security>
  );
}

// ---------------------------------------------------------------------------
// Internal: decides CAD vs Okta based on CadConfig context
// ---------------------------------------------------------------------------

function AuthSwitch({
  oktaAuth,
  children,
}: {
  oktaAuth: OktaAuth | null;
  children: ReactNode;
}) {
  const cadConfig = useCadConfig();

  if (cadConfig.enabled) {
    // Clear stale Okta session so the browser doesn't auto-redirect to Okta
    localStorage.removeItem('okta-token-storage');
    localStorage.removeItem('okta-cache-storage');
    localStorage.removeItem('okta-original-uri-storage');
    return (
      <CadAuthProvider>
        <CadAuthBridge>{children}</CadAuthBridge>
      </CadAuthProvider>
    );
  }

  // No manual CAD-session cleanup here. oidc-client-ts owns the CAD session and
  // clears it through removeUser()/clearStaleState() on sign-out; nothing
  // CAD-related is read in Okta mode. The original/return URI travels in the
  // OIDC callback state.

  if (!oktaAuth) {
    return <>{children}</>;
  }

  return (
    <OktaSecurityWrapper oktaAuth={oktaAuth}>{children}</OktaSecurityWrapper>
  );
}

// ---------------------------------------------------------------------------
// Public: AuthProvider
// ---------------------------------------------------------------------------

export interface AuthProviderProps {
  /**
   * Okta auth options. Provide null when the app does not configure Okta
   * (e.g., a CAD-only deployment). The provider builds the OktaAuth instance
   * internally so consuming apps never need to import @okta/okta-auth-js.
   *
   * Use `buildOktaConfigFromWindow()` to build this from runtime/window/env.
   */
  oktaConfig: OktaConfig | null;
  /** Override the CAD loginUrl (e.g., staff.certara.net for admin apps) */
  cadLoginUrl?: string;
  children: ReactNode;
}

/**
 * Unified auth provider. Provides CAD config, then renders either CAD or Okta
 * auth. All children use `useAuth()` for a provider-agnostic interface.
 * Api-clients use `getToken()` for token access.
 *
 * Apps pass an Okta config object; the provider constructs the OktaAuth
 * instance internally so apps never need to depend on @okta/okta-auth-js.
 */
export function AuthProvider({
  oktaConfig,
  cadLoginUrl,
  children,
}: AuthProviderProps) {
  // Build a single, stable OktaAuth instance for the lifetime of this provider.
  // useRef ensures we don't recreate it across re-renders.
  const oktaAuthRef = useRef<OktaAuth | null>(null);
  if (oktaConfig && !oktaAuthRef.current) {
    oktaAuthRef.current = new OktaAuth(oktaConfig);
  }
  const oktaAuth = oktaAuthRef.current;

  return (
    <OktaAuthInstanceContext.Provider value={oktaAuth}>
      <CadConfigProvider loginUrl={cadLoginUrl}>
        <AuthSwitch oktaAuth={oktaAuth}>{children}</AuthSwitch>
      </CadConfigProvider>
    </OktaAuthInstanceContext.Provider>
  );
}
